import { createContext, useContext, useState, ReactNode } from "react";

// ADR-21: a persistent context header on every console screen -
// Client > Engagement > Entity > Assessment run - because a consultant working
// across several clients must always be able to tell which object a control
// acts on. In the shell, screens publish their trail into this context; run
// status, assessment date and comparison baseline join the display when runs
// exist (session 2+).

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbApi {
  trail: Crumb[];
  setTrail: (trail: Crumb[]) => void;
}

const Ctx = createContext<BreadcrumbApi>({ trail: [], setTrail: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<Crumb[]>([]);
  return <Ctx.Provider value={{ trail, setTrail }}>{children}</Ctx.Provider>;
}

export function useBreadcrumb() {
  return useContext(Ctx);
}

// The header is the shell's thin top band (Workbench .topband): the trail on
// the left, whatever the shell passes as children (the user menu) on the right.
export function ContextHeader({ children }: { children?: ReactNode }) {
  const { trail } = useBreadcrumb();
  return (
    <header className="topband">
      <div className="trail">
      {trail.length === 0 ? (
        <span style={{ color: "var(--text-muted)" }}>—</span>
      ) : (
        trail.map((c, i) => (
          <span key={i} style={{ display: "inline-flex", gap: "0.4rem", alignItems: "center" }}>
            {i > 0 && <span style={{ color: "var(--text-muted)" }}>›</span>}
            <span style={{ color: i === trail.length - 1 ? "var(--text-primary)" : undefined, fontWeight: i === trail.length - 1 ? 600 : 400 }}>
              {c.label}
            </span>
          </span>
        ))
      )}
      </div>
      {children}
    </header>
  );
}
